// eventdetail.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import "./eventdetail.css";

const API_URL = import.meta.env.VITE_API_URL;

/* ========================================
 * ヘルパー関数
 * ======================================== */
const formatDate = (iso) => {
  if (!iso) return '';
  return iso.replace(/-/g, '/');
};

const formatPrice = (price) => {
  if (typeof price !== 'number') return '';
  return `${price.toLocaleString()}円`;
};

const formatMinMonths = (months) => {
  if (months === null || months === undefined) return 'なし';
  return `${months}ヶ月1日以上`;
};

const formatMaxMonths = (months) => {
  if (months === null || months === undefined) return 'なし';
  return `${months}ヶ月以下`;
};

const getEntryButtonLabel = (isInCart, applyStatus) => {
  if (applyStatus === 'before') return '申込開始前';
  if (applyStatus === 'ended') return '申込終了';
  if (isInCart) return '編集';
  return 'エントリー';
};

/**
 * 月齢を計算（event_dateとbirth_dateから）
 */
const calcMonthsAge = (eventDate, birthDate) => {
  if (!eventDate || !birthDate) return null;
  const event = new Date(eventDate);
  const birth = new Date(birthDate);
  
  let months = (event.getFullYear() - birth.getFullYear()) * 12;
  months += event.getMonth() - birth.getMonth();
  
  if (event.getDate() < birth.getDate()) {
    months -= 1;
  }
  return months;
};

/**
 * 全ての犬に選択可否フラグを付けて返す
 * ★変更: エントリー済み情報を考慮
 */
const getAllDogsWithStatus = (dogs, classItem, eventDate, enteredDogIdsForClass = []) => {
  if (!dogs || !classItem) return [];
  
  const isChampionClass = classItem.name?.includes('チャンピオン');
  
  // このクラスで既にエントリー済みの犬IDセット
  const enteredSet = new Set(enteredDogIdsForClass);
  
  return dogs.map(dog => {
    const monthsAge = calcMonthsAge(eventDate, dog.birth_date);
    let selectable = true;
    let reason = '';
    let isEntered = enteredSet.has(dog.id);  // ★追加
    
    // ★追加: このクラスで既にエントリー済みなら選択不可
    if (isEntered) {
      selectable = false;
      reason = 'エントリー済み';
    } else if (monthsAge === null) {
      selectable = false;
      reason = '生年月日不明';
    } else {
      const minMonths = classItem.min_months;
      const maxMonths = classItem.max_months;
      
      if (minMonths !== null && monthsAge < minMonths) {
        selectable = false;
        reason = `月齢不足（${monthsAge}ヶ月）`;
      } else if (minMonths !== null && monthsAge === minMonths) {
        // ちょうどNヶ月は「Nヶ月1日以上」を満たさない
        const eventD = new Date(eventDate);
        const birthD = new Date(dog.birth_date);
        if (eventD.getDate() === birthD.getDate()) {
          selectable = false;
          reason = `月齢不足（${monthsAge}ヶ月0日）`;
        }
      } else if (maxMonths !== null && monthsAge > maxMonths) {
        selectable = false;
        reason = `月齢超過（${monthsAge}ヶ月）`;
      } else if (maxMonths !== null && monthsAge === maxMonths) {
        // ちょうどNヶ月でも誕生日当日以外は日数が経過している
        const eventD = new Date(eventDate);
        const birthD = new Date(dog.birth_date);
        if (eventD.getDate() !== birthD.getDate()) {
          selectable = false;
          reason = `月齢超過（${monthsAge}ヶ月）`;
        }
      }
    }
    
    if (selectable && isChampionClass && !dog.has_champion_file) {
      selectable = false;
      reason = 'チャンピオン証書なし';
    }
    
    return {
      ...dog,
      selectable,
      reason,
      monthsAge,
      isEntered,  // ★追加
    };
  });
};

/**
 * localStorageからカートを読み込む
 */
const loadCartFromStorage = (eventId) => {
  try {
    const stored = localStorage.getItem(`event_cart_${eventId}`);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * localStorageにカートを保存
 */
const saveCartToStorage = (eventId, cart) => {
  try {
    localStorage.setItem(`event_cart_${eventId}`, JSON.stringify(cart));
  } catch {
    // ignore
  }
};

/* ========================================
 * メインコンポーネント
 * ======================================== */
export default function EventDetail({ eventId, onBack }) {
  const [cart, setCart] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // モーダル用state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDogIds, setSelectedDogIds] = useState(['']); // 配列で管理
  const [entryNote, setEntryNote] = useState(''); 

    // ★追加: カート確認モーダル用state
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [cartNotes, setCartNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [agreements, setAgreements] = useState([false, false, false, false]);

  // 初回マウント時にlocalStorageからカート読み込み
  useEffect(() => {
    if (eventId) {
      const storedCart = loadCartFromStorage(eventId);
      setCart(storedCart);
    }
  }, [eventId]);

  // カート変更時にlocalStorageに保存
  useEffect(() => {
    if (eventId) {
      saveCartToStorage(eventId, cart);
    }
  }, [eventId, cart]);

  /* ========================================
   * イベント詳細取得
   * ======================================== */
  const {
    data: event,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['event-detail', eventId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/get/${eventId}/`);
      if (!res.data?.success) throw new Error('イベント詳細の取得に失敗しました');
      return res.data.data;
    },
    enabled: !!eventId,
    retry: 1,
    staleTime: 0,
  });

  /* ========================================
   * カート内のクラスIDセット（高速検索用）
   * ======================================== */
  const cartClassIds = useMemo(() => {
    return new Set(cart.map(item => item.class_id));
  }, [cart]);

  /* ========================================
   * 指定クラスのカート内アイテムを取得
   * ======================================== */
  const getCartItemsForClass = (classId) => {
    return cart.filter(item => item.class_id === classId);
  };

  /* ========================================
   * ハンドラ
   * ======================================== */
  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    }
  };

  

  // モーダルを開く（既存データがあればロード）
  const handleOpenModal = (classItem) => {
    setSelectedClass(classItem);
    
    // 既存のカートデータをロード
    const existingItems = getCartItemsForClass(classItem.id);
    if (existingItems.length > 0) {
      setSelectedDogIds(existingItems.map(item => String(item.dog_id)));
      setEntryNote(existingItems[0].note || '');
    } else {
      setSelectedDogIds(['']);
      setEntryNote('');
    }
    
    setModalOpen(true);
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedClass(null);
    setSelectedDogIds(['']);
    setEntryNote('');
  };

  // プルダウンを追加
  const handleAddDogSelect = () => {
    setSelectedDogIds(prev => [...prev, '']);
  };

  // プルダウンを削除
  const handleRemoveDogSelect = (index) => {
    setSelectedDogIds(prev => prev.filter((_, i) => i !== index));
  };

  // プルダウンの値を変更
  const handleDogSelectChange = (index, value) => {
    setSelectedDogIds(prev => {
      const newIds = [...prev];
      newIds[index] = value;
      return newIds;
    });
  };

  // カートに入れる（該当クラスを置き換え）
  const handleAddToCart = () => {
    if (!selectedClass) return;
    
    const entryDogs = event?.entry_dogs || [];
    const validDogIds = selectedDogIds.filter(id => id !== '');
    
    // 重複を除去
    const uniqueDogIds = [...new Set(validDogIds)];
    
    // 新しいカートアイテムを作成
    const newItems = uniqueDogIds.map(dogId => {
      const dog = entryDogs.find(d => d.id === Number(dogId));
      return {
        class_id: selectedClass.id,
        class_name: selectedClass.name,
        price: selectedClass.price,
        dog_id: dog?.id,
        dog_call_name: dog?.call_name,
        note: entryNote,
      };
    });
    
    // 既存のこのクラス以外のアイテム + 新しいアイテム
    setCart(prev => {
      const otherItems = prev.filter(item => item.class_id !== selectedClass.id);
      return [...otherItems, ...newItems];
    });
    
    handleCloseModal();
  };

  // カートからクラス全体を削除
  const handleRemoveClassFromCart = (classId) => {
    setCart(prev => prev.filter(item => item.class_id !== classId));
  };

  // カート確認モーダルを開く（★変更）
  const handleCartConfirm = () => {
    if (cart.length === 0) {
      window.alert('カートが空です');
      return;
    }
    setCartModalOpen(true);
  };

  // カート確認モーダルを閉じる
  const handleCloseCartModal = () => {
    setCartModalOpen(false);
    setAgreements([false, false, false, false]); // ★追加：閉じたらリセット
  };

  // カートから個別アイテムを削除
  const handleRemoveCartItem = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  // 決済へ進む
  
  const handleProceedPayment = async () => {
    if (cart.length === 0) return;
    
    setIsSubmitting(true);
    
    try {
      // ★ Stripe Checkout Session作成API呼び出し
      const res = await axios.post(`${API_URL}/api/stripe/entry-checkout/`, {
        event_id: eventId,
        items: cart.map(item => ({
          class_id: item.class_id,
          dog_id: item.dog_id,
          price: item.price,
          note: item.note || '',
        })),
        notes: cartNotes,
      });
      
      if (res.data?.success && res.data.checkout_url) {
        // ★ Stripe決済ページへリダイレクト
        window.location.href = res.data.checkout_url;
      } else {
        window.alert(`エラー: ${res.data?.message || '決済ページの作成に失敗しました'}`);
        setIsSubmitting(false);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      const errorCode = err.response?.data?.error_code;
      
      if (errorCode === 'ENTRY_LIMIT_REACHED' || errorCode === 'ENTRY_LIMIT_EXCEEDED') {
        window.alert(errorMessage);
        setCartModalOpen(false);
      } else {
        window.alert(`エラー: ${errorMessage}`);
      }
      setIsSubmitting(false);
    }
  };

  /* ========================================
   * ローディング・エラー
   * ======================================== */
  if (isLoading) {
    return (
      <div className="eventDetailPage">
        <div className="eventDetailLoading">読み込み中...</div>
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="eventDetailPage">
        <div className="eventDetailError">
          エラー: {error?.message || 'イベント詳細の取得に失敗しました'}
        </div>
        <div className="eventDetailFooter">
          <button type="button" className="btn btn-secondary" onClick={handleBack}>
            戻る
          </button>
        </div>
      </div>
    );
  }

  const entryClasses = event.entry_classes || [];
  const displayedClasses = entryClasses.slice(0, itemsPerPage);
  const entryDogs = event.entry_dogs || [];
  const applyStatus = event.apply_status || 'accepting';

  // モーダル用：全犬リスト（選択可否付き）
  // ★変更: エントリー済み犬IDを渡す
  const dogsWithStatus = selectedClass 
    ? getAllDogsWithStatus(
        entryDogs, 
        selectedClass, 
        event.date,
        (selectedClass.entered_dogs || []).map(d => d.dog_id)  // ★追加
      )
    : [];

  // 既に選択済みの犬ID（重複選択防止用）
  const selectedDogIdSet = new Set(selectedDogIds.filter(id => id !== ''));

  /* ========================================
   * 詳細表示
   * ======================================== */
  return (
    <div className="eventDetailPage">
      <div className="eventDetailCard">
        {/* ヘッダー */}
        <div className="eventDetailHeader">
          <h1 className="eventDetailTitle">
            {event.title}
            <span>【{event.summary}】</span>
          </h1>
        </div>

        {/* 開催場所 */}
        <div className="eventDetailSection">
          <div className="eventDetailLabel">開催場所</div>
          <div className="eventDetailValue">{event.venue}</div>
        </div>

        {/* 主催者・事務所・連絡先 */}
        <div className="eventDetailSection">
          <div className="eventDetailGrid3">
            <div className="eventDetailCell">
              <div className="eventDetailLabel">主催者</div>
              <div className="eventDetailValue">{event.club_name}</div>
            </div>
            <div className="eventDetailCell">
              <div className="eventDetailLabel">事務所</div>
              <div className="eventDetailValue">{event.contact_name}</div>
            </div>
            <div className="eventDetailCell">
              <div className="eventDetailLabel">連絡先電話番号</div>
              <div className="eventDetailValue">{event.contact_phone}</div>
            </div>
          </div>
        </div>

        {/* 注意事項 */}
        <div className="eventDetailSection">
          <div className="eventDetailLabel">注意事項</div>
          <div className="eventDetailTextarea">{event.notes}</div>
        </div>

        {/* 審査委員長 */}
        <div className="eventDetailSection">
          <div className="eventDetailLabel">審査委員長</div>
          <div className="eventDetailTextarea">
            {event.judging || '-'}
          </div>
        </div>

        {/* ブリード */}
        <div className="eventDetailSection">
          <div className="eventDetailLabel">ブリード</div>
          <div className="eventDetailTextarea">{event.breed}</div>
        </div>

        {/* 開催日と申込期間 */}
        <div className="eventDetailSection">
          <div className="eventDetailSectionTitle">開催日と申込期間</div>
          <div className="eventDetailGrid3">
            <div className="eventDetailCell">
              <div className="eventDetailLabel">開催日</div>
              <div className="eventDetailValue">{formatDate(event.date)}</div>
            </div>
            <div className="eventDetailCell">
              <div className="eventDetailLabel">申込開始日</div>
              <div className="eventDetailValue">{formatDate(event.entry_start_date)}</div>
            </div>
            <div className="eventDetailCell">
              <div className="eventDetailLabel">申込終了日</div>
              <div className="eventDetailValue">{formatDate(event.entry_end_date)}</div>
            </div>
          </div>
        </div>

        {/* ★追加: 上限到達メッセージ */}
        {event.is_full && (
          <div className="eventDetailFullAlert">
            <div className="eventDetailFullAlertIcon">⚠️</div>
            <div className="eventDetailFullAlertText">
              申し込み上限数に達しておりますので主催者に直接ご連絡ください
            </div>
          </div>
        )}

        {/* 残り枠数表示（上限に近い場合） */}
        {!event.is_full && event.remaining_slots !== null && event.remaining_slots <= 10 && (
          <div className="eventDetailRemainingAlert">
            残り枠：{event.remaining_slots}頭
          </div>
        )}

        {/* 出陳料 */}
        <div className="eventDetailSection">
          <div className="eventDetailSectionTitle">出陳料</div>
          <div className="eventDetailTableWrapper">
            <table className="eventDetailTable">
              <thead>
                <tr>
                  <th>区分</th>
                  <th>価格</th>
                  <th>月齢下限</th>
                  <th>月齢上限</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {displayedClasses.map((cls) => {
                    const isInCart = cartClassIds.has(cls.id);
                    const cartItems = getCartItemsForClass(cls.id);
                    const hasEntries = cls.entry_count > 0;
                    const isChampionClass = cls.name.includes('チャンピオン'); // ★追加

                    return (
                        <React.Fragment key={cls.id}> {/* ★ key を Fragment に移動 */}
                            <tr className={`${isInCart ? 'rowInCart' : ''} ${hasEntries ? 'rowEntered' : ''}`}>
                                <td data-label="区分">
                                    {cls.name}
                                    {hasEntries && (
                                        <span className="enteredBadge">
                                            エントリー済（{cls.entry_count}頭）
                                        </span>
                                    )}
                                    {isInCart && (
                                        <span className="cartBadge">
                                            カート済（{cartItems.length}頭）
                                        </span>
                                    )}
                                    {/* ★追加: 区分名の直下に表示 */}
                                    {isChampionClass && (
                                        <div className="championNote">
                                            ※チャンピオンはチャンピオン証明書を登録しないと申し込みができません。
                                        </div>
                                    )}
                                </td>
                                <td data-label="価格">{formatPrice(cls.price)}</td>
                                <td data-label="月齢下限">{formatMinMonths(cls.min_months)}</td>
                                <td data-label="月齢上限">{formatMaxMonths(cls.max_months)}</td>
                                <td data-label="操作">
                                    <div className="actionButtons">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${isInCart ? 'btn-secondary' : 'btn-primary'}`}
                                            onClick={() => handleOpenModal(cls)}
                                            disabled={(event.is_full && !isInCart) || applyStatus !== 'accepting'}
                                        >
                                            {getEntryButtonLabel(isInCart, applyStatus)}
                                        </button>
                                        {isInCart && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleRemoveClassFromCart(cls.id)}
                                            >
                                                削除
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        </React.Fragment>
                    );
                })}
                </tbody>
            </table>
          </div>
        </div>

        {/* フッター */}
       <div className="eventDetailFooterWrap">
        <div className="eventDetailFooter">
          <button type="button" className="btn btn-secondary" onClick={handleBack}>
            戻る
          </button>
          <div className="cartButtonWrapper">
            <div className="cartNote">※イベントごとに決済してください。</div>
            <button 
              type="button" 
              className="btn btn-primary btn-cart" 
              onClick={handleCartConfirm}
              disabled={(event.is_full && cart.length === 0) || applyStatus !== 'accepting'}
            >
              カートを確認する（{cart.length}）
            </button>
          </div>
        </div>
      </div>
      </div>


      {/* エントリーモーダル */}
      {modalOpen && selectedClass && (
        <div className="entryModalOverlay" onClick={handleCloseModal}>
          <div className="entryModal" onClick={(e) => e.stopPropagation()}>
          <div className="entryModalHeader">
            <h2 className="entryModalTitle">エントリー</h2>
            <button type="button" className="entryModalClose" onClick={handleCloseModal} aria-label="閉じる">
              ✕
            </button>
          </div>
            <div className="entryModalGrid">
              <div className="entryModalField">
                <label className="entryModalLabel">クラス名</label>
                <div className="entryModalValue">{selectedClass.name}</div>
              </div>
              <div className="entryModalField">
                <label className="entryModalLabel">出陳料</label>
                <div className="entryModalValue">{selectedClass.price}</div>
              </div>
            </div>

            <div className="entryModalGrid">
              <div className="entryModalField">
                <label className="entryModalLabel">月齢下限</label>
                <div className="entryModalValue">
                  {selectedClass.min_months ?? '-'}
                </div>
              </div>
              <div className="entryModalField">
                <label className="entryModalLabel">月齢上限</label>
                <div className="entryModalValue">
                  {selectedClass.max_months ?? '-'}
                </div>
              </div>
            </div>

            {/* 複数プルダウン */}
            <div className="entryModalDogList">
              <label className="entryModalLabel">コール名</label>
              {selectedDogIds.map((dogId, index) => (
                <div key={index} className="entryModalDogRow">
                  <select
                    className="entryModalSelect"
                    value={dogId}
                    onChange={(e) => handleDogSelectChange(index, e.target.value)}
                  >
                    <option value="">選択してください</option>
                    {dogsWithStatus.map(dog => {
                      // 他の行で既に選択済みの犬は選択不可
                      const isSelectedElsewhere = dogId !== String(dog.id) && selectedDogIdSet.has(String(dog.id));
                      const isDisabled = !dog.selectable || isSelectedElsewhere;
                      
                      return (
                        <option 
                          key={dog.id} 
                          value={dog.id}
                          disabled={isDisabled}
                          className={isDisabled ? 'optionDisabled' : ''}
                        >
                          {dog.call_name}
                          {!dog.selectable && ` （${dog.reason}）`}
                          {isSelectedElsewhere && ' （選択済み）'}
                        </option>
                      );
                    })}
                  </select>
                  {selectedDogIds.length > 1 && (
                    <button
                      type="button"
                      className="entryModalBtnRemove"
                      onClick={() => handleRemoveDogSelect(index)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="entryModalActions">
              <button 
                type="button" 
                className="entryModalBtnPlus"
                onClick={handleAddDogSelect}
              >
                + 追加
              </button>
            </div>

            {/* ★追加: 備考 */}
            <div className="entryModalNotesWrap">
              <label className="entryModalLabel">備考</label>
              <textarea
                className="entryModalNotes"
                placeholder="備考を入力してください"
                value={entryNote}
                onChange={(e) => setEntryNote(e.target.value)}
                rows={3}
              />
            </div>

            <div className="entryModalFooter">
              <button
                type="button"
                className="entryModalLink"
                onClick={handleCloseModal}
              >
                閉じる
              </button>
              <button
                type="button"
                className="entryModalLink entryModalLinkPrimary"
                onClick={handleAddToCart}
                disabled={selectedDogIds.every(id => id === '')}
              >
                カートに入れる
              </button>
            </div>
          </div>
        </div>
      )}



       {/* カート確認モーダル */}
      {cartModalOpen && (
        <div className="cartModalOverlay" onClick={handleCloseCartModal}>
          <div className="cartModal" onClick={(e) => e.stopPropagation()}>
            <h2 className="cartModalTitle">決済</h2>
            
            <div className="cartModalTableWrap">
              <table className="cartModalTable">
                <thead>
                  <tr>
                    <th>クラス名</th>
                    <th className="is-right">金額</th>
                    <th>コール名</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item, index) => (
                    <tr key={index}>
                      <td>{item.class_name}</td>
                      <td className="is-right">{item.price?.toLocaleString()}</td>
                      <td>{item.dog_call_name}</td>
                      <td>
                        <button
                          type="button"
                          className="cartModalBtnCancel"
                          onClick={() => handleRemoveCartItem(index)}
                        >
                          取消
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* エントリー手数料 */}
                  <tr className="cartModalFeeRow">
                    <td>エントリー手数料</td>
                    <td className="is-right">500</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 合計とページネーション風 */}
            <div className="cartModalSummary">
              <div className="cartModalTotal">
                合計：{(cart.reduce((sum, item) => sum + (item.price || 0), 0) + 500).toLocaleString()}円
              </div>
              <div className="cartModalPager">
                <span>表示件数:</span>
                <select className="cartModalSelect" defaultValue="10">
                  <option value="10">10</option>
                </select>
                <span className="cartModalRange">1-{cart.length + 1} / {cart.length + 1}</span>
                <div className="cartModalPagerBtns">
                  <button type="button" disabled>|&lt;</button>
                  <button type="button" disabled>&lt;</button>
                  <button type="button" disabled>&gt;</button>
                  <button type="button" disabled>&gt;|</button>
                </div>
              </div>
            </div>

            {/* ★追加: 同意チェックボックス */}
            <div className="cartModalAgreements">
              {[
                '上記登録内容で間違いはございません。',
                '申し込み完了後のキャンセルは一切お受けできません。',
                '申し込み完了後の返金はできません。',
                '上記を理解した上で決済に進みます。',
              ].map((text, i) => (
                <label key={i} className="cartModalAgreementRow">
                  <input
                    type="checkbox"
                    checked={agreements[i]}
                    onChange={(e) => {
                      const next = [...agreements];
                      next[i] = e.target.checked;
                      setAgreements(next);
                    }}
                  />
                  <span>{text}</span>
                </label>
              ))}
            </div>

            {/* 備考 */}
            {/* <div className="cartModalNotesWrap">
              <textarea
                className="cartModalNotes"
                placeholder="備考"
                value={cartNotes}
                onChange={(e) => setCartNotes(e.target.value)}
              />
            </div> */}

            {/* フッター */}
            <div className="cartModalFooter">
              <button
                type="button"
                className="cartModalLink"
                onClick={handleCloseCartModal}
              >
                閉じる
              </button>
              <button
                type="button"
                className="cartModalLink cartModalLinkPrimary"
                onClick={handleProceedPayment}
                disabled={isSubmitting || cart.length === 0 || agreements.some(v => !v)}
              >
                {isSubmitting ? '処理中...' : '決済へ進む'}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}